import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import DataConnectionVersionSelector from 'components/CleanRooms/CurrentQuestions/DataConnectionVersionSelector';
import ExclusiveDatasetModeAlert
  from 'components/CleanRooms/CurrentQuestions/ExclusiveDatasetModeAlert';
import Grid from '@mui/material/Grid';
import PermissionService from 'components/Common/PermissionService';
import React, { useState } from 'react';
import SectionHeader from 'components/IAM/Admin/SectionHeader';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import moment from 'moment/moment';
import orderBy from 'lodash/orderBy';
import partitionIcon from 'assets/partition.svg';
import {
  Alert, Checkbox, Dialog, FormControlLabel,
} from '@mui/material';
import {
  BillableSourceType,
  useGetBillableConfig,
  useUpsertBillableConfig,
} from 'api/billableConfigs';
import { CurrentQuestionsContext } from 'components/CleanRooms/CurrentQuestions/CurrentQuestionsContextProvider';
import {
  DATA_CONNECTION_DATE_PARAMS,
  DATA_TYPES,
  PARAM_SUFFIX,
  autoPopulateFromRolling,
  calculateRollingDateRange,
} from 'components/CleanRooms/utils/partitionHelpers';
import {
  END_DATE_PARAMETER,
  START_DATE_PARAMETER,
  isDateParametersAdded,
} from 'components/InternalAdmin/QuestionConfigure/QuestionParameters';
import { HButton, HSpinner } from 'BaseComponents';
import { ParametersField } from 'components/CleanRooms/CurrentQuestions/NewReportRunModal/ParametersField';
import { RAMPID_UNIVERSE_DISPLAY_NAME } from 'components/DataConnection/constants';
import { canSeeBillableFeatures } from 'utils/permissionUtils';
import {
  createNewRun,
  createNewRunReset,
  fetchCrQuestionDatasets,
  fetchQuestionDetails,
} from 'redux/actions/CleanRoomsActions';
import {
  productPerm,
  userPermissions,
} from 'utils/appConstants';
import { useDispatch, useSelector } from 'react-redux';
import { useFetchCleanRoomQuestion, useFetchCleanRoomQuestionParameters } from 'hooks/questionAPIs';
import { useParams } from 'react-router-dom';
import { useValidateExclusiveDatasetMode } from 'api/questions/useValidateExclusiveDatasetMode';


const LoadingSkeleton = () => (
  <>
    <Skeleton width={350} />
    <Skeleton width={350} />
    <Box display='flex' justifyContent='flex-end'>
      <CardActions>
        <Skeleton width={50} />
        <Skeleton width={50} />
      </CardActions>
    </Box>
  </>
);

// Helper function to check if question has RampId Universe datasets
const hasRampIdUniverseDatasets = (questionDatasets) => (
  Array.isArray(questionDatasets) && questionDatasets.length > 0
  && questionDatasets.some(d => d.dataSourceName === RAMPID_UNIVERSE_DISPLAY_NAME)
);

const NewReportRunModal = ({ onReportAdded }) => {
  const { crId } = useParams();
  const [selectedVersions, setSelectedVersions] = useState({});
  const cleanroomDetail = useSelector(state => state.cleanRooms.detail);
  const crRun = useSelector(state => state.cleanRooms.crRun);
  const questionDatasets = useSelector(state => state.cleanRooms.questionDatasets);
  const orgId = useSelector(state => state.session.user.primaryOrganization.organization.ID);
  const { runModal } = React.useContext(CurrentQuestionsContext);
  const [runDetails, updateRunDetails] = React.useState({ parameters: {} });
  const [errors, setErrors] = React.useState({});
  const [isBillable, setIsBillable] = React.useState(false);
  const dispatch = useDispatch();
  const crQuestion = runModal.runModalDetails;
  const isDatasetMaterializationEnabled = PermissionService.isProductEnabled(
    [userPermissions.DatasetMaterialization],
  );
  const isIdentityResolutionEnabled = PermissionService.isProductEnabled([
    productPerm.IdentityResolution]);
  const isExclusiveDatasetModeEnabled = PermissionService.isProductEnabled([
    productPerm.ExclusiveDatasetMode,
  ]);
  const {
    isLoading: isLoadingCRQuestion,
    isFetching: isFetchingCRQuestion,
    data: { question: questionDetails },
  } = useFetchCleanRoomQuestion(
    crQuestion.questionID, crQuestion.ID, crId);
  const { data } = useFetchCleanRoomQuestionParameters(crQuestion.ID);
  const {
    isLoading: isLoadingExclusiveDatasetMode,
    isSuccess: validationSuccess,
    data: { data: validationResp } = {},
    mutate: callValidateExclusiveDatasetMode,
  } = useValidateExclusiveDatasetMode();

  const canSeeBillableCheckbox = canSeeBillableFeatures(cleanroomDetail?.type);

  const {
    data: crqBillableConfig,
    isFetching: isLoadingCrqBillableConfig,
  } = useGetBillableConfig(
    crQuestion?.ID,
    BillableSourceType.BILLABLE_CRQ,
    !!crQuestion && !!cleanroomDetail && canSeeBillableCheckbox,
  );

  // Hook to upsert billable config after run creation
  const {
    mutate: upsertBillableConfig,
    isLoading: isUpsertingBillableConfig,
  } = useUpsertBillableConfig();

  // Get cleanroom billable config for inheritance
  const {
    data: cleanRoomBillableConfig,
  } = useGetBillableConfig(
    cleanroomDetail?.ID,
    BillableSourceType.BILLABLE_CR,
    !!cleanroomDetail && !!crQuestion && canSeeBillableCheckbox,
  );

  // The date parameters are not stored in the database
  // Front end fills these 2 parameters based on the value of hasDateParameters
  if (questionDetails?.hasDateParameters && !isDateParametersAdded(questionDetails.parameters)) {
    questionDetails.parameters = [
      {
        ...START_DATE_PARAMETER,
      },
      {
        ...END_DATE_PARAMETER,
      },
      ...questionDetails.parameters,
    ];
  }

  // Determine if billable checkbox should be shown and set default value
  const shouldShowBillableCheckbox = React.useMemo(() => {
    if (!canSeeBillableCheckbox) {
      return false;
    }

    if (crqBillableConfig) {
      return crqBillableConfig.value === 'true';
    }

    // Check cleanroom billable config if no CRQ config
    return cleanRoomBillableConfig?.value === 'true';
  }, [canSeeBillableCheckbox, crqBillableConfig, cleanRoomBillableConfig]);

  // Default value is always true when checkbox is shown (since we only show when config is true)
  const defaultBillableValue = shouldShowBillableCheckbox;

  // Check if partition parameters contain data connection date parameters
  const hasDataConnectionDateParams = React.useMemo(() => {
    if (!data?.length) {
      return false;
    }
    return data.some(
      param => param.name.endsWith(DATA_CONNECTION_DATE_PARAMS.START)
        || param.name.endsWith(DATA_CONNECTION_DATE_PARAMS.END),
    );
  }, [data]);

  const onClose = React.useCallback(() => {
    dispatch(createNewRunReset());
    runModal.setRunModalDetails(undefined);
    onReportAdded();
  }, [dispatch, runModal, onReportAdded]);

  const onSave = (e) => {
    e.preventDefault();

    // Check if any assigned dataset uses RampId Universe datasource
    const hasRampIdUniverse = hasRampIdUniverseDatasets(questionDatasets);

    const newRunDetails = ((isIdentityResolutionEnabled && questionDetails.hasLinkedDatasets)
      || hasRampIdUniverse)
      ? {
        ...runDetails,
        parameters: {
          ...runDetails.parameters,
          RESOLUTION_DATASET_VERSIONS: JSON.stringify(Object.values(selectedVersions)),
        },
      } : runDetails;

    if (isExclusiveDatasetModeEnabled) {
      const payload = {
        crId: crQuestion.cleanRoomID,
        crqId: crQuestion.ID,
      };
      callValidateExclusiveDatasetMode(payload, {
        onSuccess: (response) => {
          if (response.data?.isValid) {
            dispatch(createNewRun(orgId, crQuestion.cleanRoomID, crQuestion.ID,
              { runDetails: newRunDetails }));
          }
        },
      });
      return;
    }

    dispatch(createNewRun(orgId, crQuestion.cleanRoomID, crQuestion.ID,
      { runDetails: newRunDetails }));
  };

  React.useEffect(() => {
    dispatch(fetchQuestionDetails(crQuestion.questionID, cleanroomDetail.ID, 0));
    dispatch(fetchCrQuestionDatasets(orgId, cleanroomDetail.ID, crQuestion.ID));
  }, [dispatch, crQuestion, cleanroomDetail.ID, orgId]);

  React.useEffect(() => {
    if (crRun?.ID) {
      if (shouldShowBillableCheckbox && !isBillable) {
        const billableConfig = {
          sourceID: crRun.ID,
          sourceType: BillableSourceType.BILLABLE_RUN,
          value: 'false',
        };

        upsertBillableConfig(billableConfig);
      }
      onClose();
    }
  }, [crRun, onClose, shouldShowBillableCheckbox, isBillable, upsertBillableConfig]);

  // Set initial billable state based on default config
  React.useEffect(() => {
    setIsBillable(defaultBillableValue);
  }, [defaultBillableValue]);

  React.useEffect(() => {
    if (isDatasetMaterializationEnabled && data?.length > 0) {
      updateRunDetails((prevRunDetails) => {
        const updatedParameters = data.reduce((acc, param) => {
          if (Object.prototype.hasOwnProperty.call(param, 'value')) {
            acc[param.name] = param.value;
          }
          return acc;
        }, { ...prevRunDetails.parameters });

        // Auto-populate start/end dates from rolling values
        const finalParameters = autoPopulateFromRolling(updatedParameters, data);

        return { ...prevRunDetails, parameters: finalParameters };
      });
    }
  }, [data, isDatasetMaterializationEnabled]);

  const getParameterConstraints = (i) => {
    const defaultConstraints = {
      minDate: null, maxDate: null, allowedValues: null, rollingDays: null,
    };

    if (![DATA_TYPES.DATE, DATA_TYPES.TIMESTAMP].includes(i.dataType)) {
      return {
        ...defaultConstraints,
        allowedValues: i.value.split(',').map(opt => opt.trim()),
      };
    }

    const match = i.name.match(/^(.*)_(start|end)$/);
    if (!match) return defaultConstraints;

    const prefix = match[1];
    const rollingParam = data?.find(param => param.name === `${prefix}${PARAM_SUFFIX.ROLLING}`);

    // Check for rolling constraints first
    if (rollingParam?.value && rollingParam.value !== '') {
      const dateRange = calculateRollingDateRange(rollingParam.value);
      if (dateRange) {
        return {
          minDate: dateRange.startDate,
          maxDate: dateRange.endDate,
          allowedValues: null,
          rollingDays: parseInt(rollingParam.value, 10),
        };
      }
    }

    // Fallback to start/end constraints
    const startParam = data?.find(param => param.name === `${prefix}${PARAM_SUFFIX.START}`);
    const endParam = data?.find(param => param.name === `${prefix}${PARAM_SUFFIX.END}`);

    return {
      minDate: startParam?.value ? moment.utc(startParam.value) : null,
      maxDate: endParam?.value ? moment.utc(endParam.value) : null,
      allowedValues: null,
      rollingDays: null,
    };
  };


  return (
    <Dialog
      open={Boolean(crQuestion)}
      onClose={onClose}
      fullWidth
      maxWidth='md'
    >
      <form onSubmit={onSave}>
        <Card>
          <CardContent>
            <SectionHeader title='New Report Run' />
            {(!questionDetails || isLoadingCRQuestion || isFetchingCRQuestion)
              ? <LoadingSkeleton />
              : (
                <>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <TextField
                        data-testid='report-text-field'
                        fullWidth
                        label='Run Name'
                        margin='normal'
                        onChange={e => updateRunDetails({ ...runDetails, name: e.target.value })}
                        required
                        type='text'
                        value={runDetails.name || ''}
                        variant='outlined'
                      />
                    </Grid>
                    {shouldShowBillableCheckbox && (
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={isBillable}
                              onChange={(e) => setIsBillable(e.target.checked)}
                              color='primary'
                              disabled={isLoadingCrqBillableConfig}
                            />
                          )}
                          label='Billable'
                          sx={{ mt: 1 }}
                        />
                      </Grid>
                    )}
                    {orderBy(questionDetails.parameters, ['index'], ['asc'])
                      .filter(i => {
                        // If hasDateParameters is true and partition params have
                        // data connection dates, filter out START_DATE and END_DATE
                        // runtime parameters
                        if (questionDetails?.hasDateParameters && hasDataConnectionDateParams) {
                          return i.name !== START_DATE_PARAMETER.name
                            && i.name !== END_DATE_PARAMETER.name;
                        }
                        return true;
                      })
                      .map(i => (
                        <Grid item xs={6} key={i.name}>
                          <ParametersField
                            name={i.name}
                            constraints={{ minDate: null, maxDate: null, allowedValues: [''] }}
                            dataType={i.type}
                            setErrors={setErrors}
                            runDetails={runDetails}
                            updateRunDetails={updateRunDetails}
                            description={i.description}
                            exampleValue={i.exampleValue}
                          />
                        </Grid>
                      ))}
                    {(() => {
                      const filteredPartitionParams = data?.length > 0
                        ? orderBy(data, ['index'], ['asc'])
                          .filter(i => {
                            // Filter out rolling parameters
                            if (i.name.endsWith(PARAM_SUFFIX.ROLLING)) {
                              return false;
                            }
                            // If !hasDateParameters, filter out data connection date params
                            if (!questionDetails?.hasDateParameters) {
                              return !i.name.endsWith(DATA_CONNECTION_DATE_PARAMS.START)
                                && !i.name.endsWith(DATA_CONNECTION_DATE_PARAMS.END);
                            }
                            return true;
                          })
                        : [];

                      return filteredPartitionParams.length > 0 && (
                        <>
                          <Grid item xs={12} mt={2}>
                            <Box display='flex' gap={1} alignItems='flex-start'>
                              <img alt='' height='24px' src={partitionIcon} width='24px' />
                              <SectionHeader title='Partition Parameters' />
                            </Box>
                          </Grid>
                          {filteredPartitionParams.map(i => {
                            const isDateOrTimestamp = [DATA_TYPES.DATE, DATA_TYPES.TIMESTAMP]
                              .includes(i.dataType);
                            return (
                              <Grid
                                item
                                xs={isDateOrTimestamp ? 6 : 12}
                                key={i.name}
                              >
                                <ParametersField
                                  name={i.name}
                                  constraints={getParameterConstraints(i)}
                                  dataType={i.dataType}
                                  setErrors={setErrors}
                                  runDetails={runDetails}
                                  updateRunDetails={updateRunDetails}
                                  description={i.description}
                                  exampleValue={i.exampleValue}
                                />
                              </Grid>
                            );
                          })}
                        </>
                      );
                    })()}
                  </Grid>
                  {crRun?.error ? (
                    <Box display='flex' justifyContent='flex-end'>
                      <Typography color='error'>{crRun?.error}</Typography>
                    </Box>
                  ) : null}
                  {/* Data Connection version selector for Linked Data Connection or RAMPID */}
                  {
                    ((isIdentityResolutionEnabled && questionDetails.hasLinkedDatasets)
                      || hasRampIdUniverseDatasets(questionDatasets)) && (
                      <DataConnectionVersionSelector
                        cleanRoomId={cleanroomDetail.ID}
                        questionId={questionDetails.ID}
                        selectedVersions={selectedVersions}
                        setSelectedVersions={setSelectedVersions}
                      />
                    )
                  }
                  {isExclusiveDatasetModeEnabled
                    && validationSuccess
                    && !validationResp?.isValid
                    && (
                      <ExclusiveDatasetModeAlert
                        violationMsg={validationResp.details?.violationMsg}
                        rampIdDatasets={validationResp.details?.rampIdDatasets}
                        piiDatasets={validationResp.details?.piiDatasets}
                      />
                    )}
                  {Object.entries(errors).length > 0 && (
                    <Box sx={{
                      maxHeight: 180, overflowY: 'auto', mt: 2, mb: 1,
                    }}
                    >
                      {Object.entries(errors).map(([key, err]) => (
                        <Alert key={key} severity='error' sx={{ mb: 2 }}>{`${key}: ${err}`}</Alert>
                      ))}
                    </Box>
                  )}
                  <Box display='flex' justifyContent='flex-end'>
                    <CardActions>
                      <HButton onClick={onClose} variant='contained' color='inherit'>Cancel</HButton>
                      <HButton
                        data-testid='report-save-button'
                        color='primary'
                        disabled={crRun?.loading
                          || isLoadingExclusiveDatasetMode
                          || isUpsertingBillableConfig
                          || (Object.values(errors).length > 0)}
                        endIcon={(crRun?.loading
                          || isLoadingExclusiveDatasetMode
                          || isUpsertingBillableConfig)
                          ? <HSpinner isButton /> : null}
                        type='submit'
                        variant='contained'
                      >
                        Save
                      </HButton>
                    </CardActions>
                  </Box>
                </>
              )}
          </CardContent>
        </Card>
      </form>
    </Dialog>
  );
};

export default NewReportRunModal;
